import { FC, useState, useEffect } from 'react';
import { Stack, TextField, Typography } from '@mui/material';

import Modal from '@/components/ui/Modal';
import CTAButton from '@/components/ui/CTAButton';
import { dsTextFieldSx } from '@/components/ui/shared/filterStyles';
import api from '@/services/api';
import useAuthStore from '@/store/auth';
import { User } from '@/types';

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDialog: FC<ProfileDialogProps> = ({ open, onClose }) => {
  const user = useAuthStore((s) => s.user);
  const [zoomLink, setZoomLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [passwortOffen, setPasswortOffen] = useState(false);
  const [aktuellesPasswort, setAktuellesPasswort] = useState('');
  const [neuesPasswort, setNeuesPasswort] = useState('');
  const [neuesPasswortWdh, setNeuesPasswortWdh] = useState('');
  const [passwortLaeuft, setPasswortLaeuft] = useState(false);
  const [passwortFehler, setPasswortFehler] = useState('');
  const [passwortErfolg, setPasswortErfolg] = useState('');

  const passwortFelderLeeren = () => {
    setAktuellesPasswort('');
    setNeuesPasswort('');
    setNeuesPasswortWdh('');
    setPasswortFehler('');
    setPasswortErfolg('');
  };

  useEffect(() => {
    if (open && user) {
      setZoomLink(user.personal_zoom_link ?? '');
      setError('');
      setPasswortOffen(false);
      passwortFelderLeeren();
    }
  }, [open, user]);

  const handleClose = () => {
    onClose();
    setError('');
    setPasswortOffen(false);
    passwortFelderLeeren();
  };

  const togglePasswort = () => {
    setPasswortOffen((offen) => !offen);
    passwortFelderLeeren();
  };

  const handlePasswortSpeichern = async () => {
    setPasswortFehler('');
    setPasswortErfolg('');

    if (neuesPasswort.length < 8) {
      setPasswortFehler('Das neue Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (neuesPasswort !== neuesPasswortWdh) {
      setPasswortFehler('Die Wiederholung stimmt nicht mit dem neuen Passwort überein.');
      return;
    }

    setPasswortLaeuft(true);
    try {
      await api.user.changePassword({
        current_password: aktuellesPasswort,
        password: neuesPasswort,
        password_confirmation: neuesPasswortWdh,
      });
      passwortFelderLeeren();
      setPasswortOffen(false);
      // NACH passwortFelderLeeren() setzen — der Helfer raeumt auch Meldungen.
      setPasswortErfolg('Passwort geändert.');
    } catch (e: unknown) {
      const err = e as { message?: string };
      setPasswortFehler(err.message ?? 'Passwort ändern fehlgeschlagen');
    } finally {
      setPasswortLaeuft(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const updated = await api.user.updateProfile({
        personal_zoom_link: zoomLink.trim() || null,
      } as Partial<User>);
      useAuthStore.setState({ user: { ...user!, personal_zoom_link: updated.personal_zoom_link } });
      handleClose();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Profil bearbeiten"
      onClose={handleClose}
      icon="AccountCircle"
      fullWidth
      maxWidth="sm"
    >
      <Stack gap={4}>
        <Stack gap={1}>
          <Typography variant="label">
            Name
          </Typography>
          <TextField
            value={user?.name ?? ''}
            size="small"
            fullWidth
            disabled
            sx={dsTextFieldSx}
            inputProps={{ 'aria-label': 'Name' }}
          />
        </Stack>
        <Stack gap={1}>
          <Typography variant="label">
            E-Mail
          </Typography>
          <TextField
            value={user?.email ?? ''}
            size="small"
            fullWidth
            disabled
            sx={dsTextFieldSx}
            inputProps={{ 'aria-label': 'E-Mail' }}
          />
        </Stack>
        <Stack gap={1}>
          <Typography variant="label">
            Persoenlicher Zoom-Link
          </Typography>
          <TextField
            value={zoomLink}
            onChange={(e) => setZoomLink(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://zoom.us/j/1234567890"
            autoFocus
            sx={dsTextFieldSx}
            inputProps={{ 'aria-label': 'Persoenlicher Zoom-Link' }}
          />
          <Typography variant="klein">
            Wird automatisch fuer Meetings verwendet
          </Typography>
        </Stack>
        {/* Passwort aendern (FB Thorluemke 05.08.) — eingeklappt, damit der
            Dialog fuer den haeufigen Fall (Zoom-Link) unveraendert kurz bleibt. */}
        <Stack gap={1} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="label">
              Passwort
            </Typography>
            <CTAButton
              label={passwortOffen ? 'Abbrechen' : 'Passwort ändern'}
              variant="tertiary"
              onClick={togglePasswort}
              autoWidth
            />
          </Stack>
          {passwortOffen && (
            <Stack gap={4} sx={{ mt: 2 }}>
              <TextField
                type="password"
                value={aktuellesPasswort}
                onChange={(e) => setAktuellesPasswort(e.target.value)}
                size="small"
                fullWidth
                placeholder="Aktuelles Passwort"
                autoComplete="current-password"
                sx={dsTextFieldSx}
                inputProps={{ 'aria-label': 'Aktuelles Passwort' }}
              />
              <TextField
                type="password"
                value={neuesPasswort}
                onChange={(e) => setNeuesPasswort(e.target.value)}
                size="small"
                fullWidth
                placeholder="Neues Passwort (mindestens 8 Zeichen)"
                autoComplete="new-password"
                sx={dsTextFieldSx}
                inputProps={{ 'aria-label': 'Neues Passwort' }}
              />
              <TextField
                type="password"
                value={neuesPasswortWdh}
                onChange={(e) => setNeuesPasswortWdh(e.target.value)}
                size="small"
                fullWidth
                placeholder="Neues Passwort wiederholen"
                autoComplete="new-password"
                sx={dsTextFieldSx}
                inputProps={{ 'aria-label': 'Neues Passwort wiederholen' }}
              />
              <Typography variant="klein">
                Nach der Änderung werden alle anderen Geräte abgemeldet.
              </Typography>
              {passwortFehler && (
                <Typography variant="wert" sx={{ color: 'error.main' }}>
                  {passwortFehler}
                </Typography>
              )}
              <Stack direction="row" justifyContent="flex-end">
                <CTAButton
                  label="Passwort speichern"
                  variant="outlined"
                  onClick={handlePasswortSpeichern}
                  disabled={passwortLaeuft}
                  loading={passwortLaeuft}
                  autoWidth
                />
              </Stack>
            </Stack>
          )}
          {/* Erfolg steht AUSSERHALB des Klappbereichs — nach dem Speichern
              klappt er zu, eine Meldung darin waere unsichtbar. */}
          {passwortErfolg && (
            <Typography variant="wert" sx={{ color: 'success.main', mt: 2 }}>
              {passwortErfolg}
            </Typography>
          )}
        </Stack>
        {error && (
          <Typography variant="wert" sx={{ color: 'error.main' }}>
            {error}
          </Typography>
        )}
        <Stack direction="row" gap={2} justifyContent="flex-end">
          <CTAButton label="Abbrechen" variant="tertiary" onClick={handleClose} autoWidth />
          <CTAButton
            label="Speichern"
            variant="secondary"
            onClick={handleSubmit}
            disabled={submitting}
            loading={submitting}
            autoWidth
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ProfileDialog;
